import { useState, type FormEvent } from 'react';
import {
  confirmSignUp,
  confirmPassword,
  forgotPassword,
  isUnconfirmedUser,
  resendCode,
  signIn,
  signUp,
} from '../auth.js';
import { Banner, Button, DropLogo, Field, TextInput } from '../components/ui.js';
import { errorMessage } from '../lib/format.js';

type Mode = 'signin' | 'signup' | 'verify' | 'forgot' | 'reset';

const COPY: Record<Mode, { title: string; sub: string; cta: string }> = {
  signin: { title: 'Welcome back', sub: 'Sign in to your irrigation dashboard.', cta: 'Sign in' },
  signup: { title: 'Create your account', sub: 'Weather-smart watering in a few minutes.', cta: 'Create account' },
  verify: { title: 'Verify your email', sub: 'We emailed you a six-digit code.', cta: 'Verify & sign in' },
  forgot: { title: 'Reset password', sub: "We'll email you a reset code.", cta: 'Send reset code' },
  reset: { title: 'Choose a new password', sub: 'Enter the emailed code and a new password.', cta: 'Set new password' },
};

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const switchMode = (m: Mode) => {
    setMode(m);
    setError('');
    setNotice('');
    setCode('');
    setShowPassword(false);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e, 'Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    switch (mode) {
      case 'signin':
        void run(async () => {
          try {
            await signIn(email.trim(), password);
            onSignedIn();
          } catch (e) {
            if (!isUnconfirmedUser(e)) throw e;
            // The account exists but was never verified — almost always someone
            // who closed the tab before entering the code. Resume the flow with
            // a fresh code instead of dead-ending on "User is not confirmed",
            // which has no self-service way out (password reset fails too).
            // The password they just typed carries into the verify step.
            switchMode('verify');
            try {
              await resendCode(email.trim());
              setNotice("Your email isn't verified yet — we've sent you a new code.");
            } catch {
              setNotice("Your email isn't verified yet. Use “Resend code” to get a new one.");
            }
          }
        });
        break;
      case 'signup':
        void run(async () => {
          await signUp(email.trim(), password);
          setNotice('Check your email for a verification code.');
          setMode('verify');
        });
        break;
      case 'verify':
        void run(async () => {
          await confirmSignUp(email.trim(), code.trim());
          await signIn(email.trim(), password);
          onSignedIn();
        });
        break;
      case 'forgot':
        void run(async () => {
          await forgotPassword(email.trim());
          setNotice('Check your email for a reset code.');
          setMode('reset');
        });
        break;
      case 'reset':
        void run(async () => {
          await confirmPassword(email.trim(), code.trim(), password);
          setNotice('Password reset — you can sign in now.');
          setMode('signin');
          setPassword('');
          setCode('');
        });
        break;
    }
  };

  const askEmail = mode === 'signin' || mode === 'signup' || mode === 'forgot';
  const askPassword = mode === 'signin' || mode === 'signup' || mode === 'reset';
  const askCode = mode === 'verify' || mode === 'reset';

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          <DropLogo size={40} />
          <h1>{COPY[mode].title}</h1>
          <div className="tagline">{COPY[mode].sub}</div>
        </div>

        <form className="card stack" onSubmit={submit}>
          {error && <Banner tone="error">{error}</Banner>}
          {notice && <Banner tone="ok">{notice}</Banner>}

          {askEmail && (
            <Field label="Email">
              {(id) => (
                <TextInput
                  id={id}
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              )}
            </Field>
          )}

          {askCode && (
            <Field label={`Verification code sent to ${email || 'your email'}`}>
              {(id) => (
                <TextInput
                  id={id}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                />
              )}
            </Field>
          )}

          {askPassword && (
            <Field
              label={mode === 'reset' ? 'New password' : 'Password'}
              hint={mode !== 'signin' ? 'At least 8 characters.' : undefined}
            >
              {(id) => (
                <div className="input-wrap">
                  <TextInput
                    id={id}
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={mode === 'signin' ? undefined : 8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    className="input-affix"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              )}
            </Field>
          )}

          <Button type="submit" busy={busy} block>
            {COPY[mode].cta}
          </Button>

          {mode === 'verify' && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await resendCode(email.trim());
                  // `run` clears the banner first, so without this the button
                  // looks like it did nothing.
                  setNotice('New code sent — check your email.');
                })
              }
            >
              Resend code
            </Button>
          )}
        </form>

        <div className="auth-footer">
          {mode === 'signin' ? (
            <>
              <button type="button" className="linklike" onClick={() => switchMode('signup')}>
                Create an account
              </button>
              {' · '}
              <button type="button" className="linklike" onClick={() => switchMode('forgot')}>
                Forgot password?
              </button>
            </>
          ) : (
            <button type="button" className="linklike" onClick={() => switchMode('signin')}>
              ← Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
